import type { OrgMember } from '@/types/api';

/**
 * Settings → Users table pipeline (A2F-698).
 *
 * Pure helpers — sort + filter + search + paginate — composed into one
 * `applyPipeline` call. URL search-params are the source of truth for
 * state; `parseStateFromUrl` reads them and `serializeStateToUrl` writes
 * them back.
 *
 * IMPORTANT: client-side at this scale (hundreds of rows). At 10K+ Pro
 * users we build a separate Pro-admin surface that lives outside the
 * main Chrona app — do NOT migrate this page to server-side pagination,
 * replace it. The cost of reshaping every helper to be async + paginated
 * server-side would dwarf the cost of the eventual replacement.
 */

export type SortKey = 'user' | 'role' | 'status' | 'lastLogin';
export type SortOrder = 'asc' | 'desc';
export type StatusFilter = 'active' | 'invited' | 'disabled';

export const ALL_STATUSES: StatusFilter[] = ['active', 'invited', 'disabled'];

export const DEFAULT_PAGE_SIZE = 50;

export interface TableState {
  search: string;
  statuses: Set<StatusFilter>;
  sortKey: SortKey;
  sortOrder: SortOrder;
  page: number;
}

export interface PipelineResult {
  /** Rows on the current page after filter/sort/search/paginate. */
  visible: OrgMember[];
  /** Row count after filter/search but before pagination. */
  totalAfterFilter: number;
  /** Total pages given the filtered count + pageSize. */
  pageCount: number;
  /** Current page clamped to [1, pageCount] (zero-pages → page 1, no rows). */
  page: number;
}

const SORT_KEY_PARAM = 'sort';
const SORT_ORDER_PARAM = 'order';
const STATUS_PARAM = 'status';
const SEARCH_PARAM = 'q';
const PAGE_PARAM = 'page';

const VALID_SORT_KEYS: ReadonlySet<SortKey> = new Set([
  'user',
  'role',
  'status',
  'lastLogin',
]);

const ROLE_RANK: Record<string, number> = {
  admin: 4,
  lead: 3,
  contributor: 2,
  viewer: 1,
  author: 0,
};

const STATUS_RANK: Record<string, number> = {
  active: 0,
  invited: 1,
  disabled: 2,
};

/**
 * Build a TableState from URL search params. Falls back to defaults when
 * a param is missing, malformed, or invalid (silently — invalid URL state
 * shouldn't break the page).
 */
export function parseStateFromUrl(params: URLSearchParams): TableState {
  const sortKey = params.get(SORT_KEY_PARAM) as SortKey | null;
  const sortOrder = params.get(SORT_ORDER_PARAM) as SortOrder | null;
  const statusRaw = params.get(STATUS_PARAM);
  const search = params.get(SEARCH_PARAM) ?? '';
  const pageRaw = params.get(PAGE_PARAM);

  const statuses = (() => {
    if (!statusRaw) return new Set(ALL_STATUSES);
    const parts = statusRaw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is StatusFilter => ALL_STATUSES.includes(s as StatusFilter));
    return parts.length > 0 ? new Set(parts as StatusFilter[]) : new Set(ALL_STATUSES);
  })();

  const page = (() => {
    const n = pageRaw ? parseInt(pageRaw, 10) : 1;
    return Number.isFinite(n) && n >= 1 ? n : 1;
  })();

  return {
    search,
    statuses,
    sortKey: sortKey && VALID_SORT_KEYS.has(sortKey) ? sortKey : 'status',
    sortOrder: sortOrder === 'desc' ? 'desc' : 'asc',
    page,
  };
}

/**
 * Mutate a URLSearchParams in place to reflect a partial state change.
 * Pass only the keys that changed; defaults are stripped from the URL so
 * the visible URL stays clean (`?` rather than `?page=1&order=asc`).
 */
export function writeStateToParams(
  params: URLSearchParams,
  next: Partial<TableState>,
): URLSearchParams {
  const out = new URLSearchParams(params);

  if (next.sortKey !== undefined) {
    if (next.sortKey === 'status') out.delete(SORT_KEY_PARAM);
    else out.set(SORT_KEY_PARAM, next.sortKey);
  }
  if (next.sortOrder !== undefined) {
    if (next.sortOrder === 'asc') out.delete(SORT_ORDER_PARAM);
    else out.set(SORT_ORDER_PARAM, next.sortOrder);
  }
  if (next.statuses !== undefined) {
    const arr = Array.from(next.statuses);
    if (arr.length === 0 || arr.length === ALL_STATUSES.length) {
      out.delete(STATUS_PARAM);
    } else {
      out.set(STATUS_PARAM, arr.join(','));
    }
  }
  if (next.search !== undefined) {
    if (!next.search) out.delete(SEARCH_PARAM);
    else out.set(SEARCH_PARAM, next.search);
  }
  if (next.page !== undefined) {
    if (next.page <= 1) out.delete(PAGE_PARAM);
    else out.set(PAGE_PARAM, String(next.page));
  }
  return out;
}

function compareUser(a: OrgMember, b: OrgMember): number {
  const an = (a.displayName ?? a.email ?? '').toLowerCase();
  const bn = (b.displayName ?? b.email ?? '').toLowerCase();
  return an < bn ? -1 : an > bn ? 1 : 0;
}

function compareLastLogin(a: OrgMember, b: OrgMember): number {
  // Nulls (never logged in) sort to the bottom regardless of order — UX
  // expectation is "old logins first or recent first," not "missing data
  // somewhere in the middle."
  if (!a.lastLoginAt && !b.lastLoginAt) return 0;
  if (!a.lastLoginAt) return 1;
  if (!b.lastLoginAt) return -1;
  return a.lastLoginAt < b.lastLoginAt ? -1 : a.lastLoginAt > b.lastLoginAt ? 1 : 0;
}

function compareByKey(a: OrgMember, b: OrgMember, key: SortKey): number {
  switch (key) {
    case 'user':
      return compareUser(a, b);
    case 'role':
      return (ROLE_RANK[a.role] ?? -1) - (ROLE_RANK[b.role] ?? -1);
    case 'status':
      return (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
    case 'lastLogin':
      return compareLastLogin(a, b);
  }
}

function matchesSearch(member: OrgMember, query: string): boolean {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = (member.displayName ?? '').toLowerCase();
  const email = (member.email ?? '').toLowerCase();
  return name.includes(q) || email.includes(q);
}

/**
 * Run the full pipeline. Caller passes the tab-filtered row set (audience
 * tab filtering is upstream); this function handles status filter →
 * search → sort → page. Self-row stays in the input — caller decides
 * whether to extract it for special rendering.
 */
export function applyPipeline(
  rows: OrgMember[],
  state: TableState,
  pageSize: number = DEFAULT_PAGE_SIZE,
): PipelineResult {
  // Status filter
  const filtered = rows.filter((m) => {
    if (state.statuses.size === 0) return false;
    const status = m.status as StatusFilter;
    return state.statuses.has(status);
  });

  // Search
  const searched = state.search
    ? filtered.filter((m) => matchesSearch(m, state.search))
    : filtered;

  // Sort
  const sorted = [...searched].sort((a, b) => {
    const cmp = compareByKey(a, b, state.sortKey);
    return state.sortOrder === 'desc' ? -cmp : cmp;
  });

  // Paginate
  const totalAfterFilter = sorted.length;
  const pageCount = Math.max(1, Math.ceil(totalAfterFilter / pageSize));
  const page = Math.min(Math.max(1, state.page), pageCount);
  const start = (page - 1) * pageSize;
  const visible = sorted.slice(start, start + pageSize);

  return { visible, totalAfterFilter, pageCount, page };
}

/**
 * Per-status counts for the filter pills. Computed against the input
 * row set BEFORE the status filter is applied so the chip counts don't
 * collapse to zero when the user toggles a status off.
 */
export function statusCounts(
  rows: OrgMember[],
): Record<StatusFilter, number> {
  const counts: Record<StatusFilter, number> = {
    active: 0,
    invited: 0,
    disabled: 0,
  };
  for (const m of rows) {
    if (m.status === 'active' || m.status === 'invited' || m.status === 'disabled') {
      counts[m.status as StatusFilter] += 1;
    }
  }
  return counts;
}
